/**
 * About page — app version, runtime info, changelog, and SBOM downloads
 */
import { useState, useEffect } from 'react'
import api from '../services/api'

interface VersionInfo {
  app_version: string
  db_schema_version: string
  db_schema_revision: number | null
  environment: string
  python_version: string
  fastapi_version: string
}

export default function AboutPage() {
  const [info, setInfo] = useState<VersionInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .get<VersionInfo>('/v1/version')
      .then((r) => setInfo(r.data))
      .catch(() => setError('Could not load version info'))
      .finally(() => setLoading(false))
  }, [])

  const apiBase = api.defaults.baseURL ?? ''

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">About</h1>

      {/* App identity */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/favicon.svg" alt="" aria-hidden="true" className="h-12 w-12 flex-shrink-0" />
          <h2 className="text-xl font-semibold">FCC License Manager</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          A secure, self-hosted web application for tracking FCC license expirations and managing
          client renewals for a Motorola radio shop.
        </p>

        {loading && <p className="text-sm text-gray-500">Loading version info…</p>}
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        {info && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="font-medium text-gray-500">App Version</dt>
              <dd className="mt-0.5 font-mono text-gray-900">{info.app_version}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Environment</dt>
              <dd className="mt-0.5">
                <span
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    info.environment === 'production'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {info.environment}
                </span>
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">DB Schema Version</dt>
              <dd className="mt-0.5 font-mono text-gray-900 break-all">
                {info.db_schema_revision != null && (
                  <span className="inline-block mr-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-semibold">
                    rev #{info.db_schema_revision}
                  </span>
                )}
                {info.db_schema_version}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Python Version</dt>
              <dd className="mt-0.5 font-mono text-gray-900">{info.python_version}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">FastAPI Version</dt>
              <dd className="mt-0.5 font-mono text-gray-900">{info.fastapi_version}</dd>
            </div>
          </dl>
        )}
      </div>

      {/* Downloads */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Downloads</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={`${apiBase}/v1/version/changelog`}
            download="CHANGELOG.md"
            className="btn btn-secondary text-sm inline-flex items-center gap-2"
          >
            <DownloadIcon />
            Changelog (CHANGELOG.md)
          </a>
          <a
            href={`${apiBase}/v1/version/sbom`}
            download="SBOM.md"
            className="btn btn-secondary text-sm inline-flex items-center gap-2"
          >
            <DownloadIcon />
            Software Bill of Materials (SBOM.md)
          </a>
        </div>
      </div>

      {/* Links */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Resources</h2>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>
            <span className="font-medium">FCC ULS License Search:</span>{' '}
            <a
              href="https://wireless2.fcc.gov/UlsApp/UlsSearch/searchLicense.jsp"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              wireless2.fcc.gov
            </a>
          </li>
          <li>
            <span className="font-medium">FCC ULS Daily/Weekly Downloads:</span>{' '}
            <a
              href="https://www.fcc.gov/uls/transactions/daily-weekly"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              fcc.gov/uls/transactions
            </a>
          </li>
        </ul>
      </div>
    </div>
  )
}

function DownloadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
  )
}
